/**
 * Display rules from the product guide §9 and the prototype's helpers:
 * money as ₹ with L for lakh (Cr from 100 L), percentages without decimals, dates as "Mon 5 Oct".
 */

/** Prototype `rv()`: 102 → "₹1.02 Cr", 96 → "₹96 L". */
export function rupees(lakh: number): string {
  if (lakh >= 100) return '₹' + (lakh / 100).toFixed(2).replace(/0$/, '') + ' Cr';
  return '₹' + (Number.isInteger(lakh) ? lakh : Number(lakh.toFixed(2))) + ' L';
}

export const pct = (n: number): string => `${Math.round(n)}%`;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `YYYY-MM-DD` (a business date in Asia/Kolkata) → its parts, without any timezone shift. */
function parts(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return { y: y ?? 1970, m: (m ?? 1) - 1, d: d ?? 1 };
}

/** "Mon 5 Oct". */
export function dayLabel(iso: string): string {
  const { y, m, d } = parts(iso);
  return `${DOW[new Date(Date.UTC(y, m, d)).getUTCDay()]} ${d} ${MON[m]}`;
}

/** "Sep 2024" — the prototype's "since" style. */
export function monthYear(iso: string): string {
  const { y, m } = parts(iso);
  return `${MON[m]} ${y}`;
}

/** First name, as the prototype's `first()` prints people in lists. */
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** Prototype `initials()` for client logos: first letters of the first two words, skipping and/of/the. */
export const logoInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter((w) => w && !/^(and|of|the)$/i.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/** Prototype LOGOCOL. The prototype picks by list position; we hash the id so a client keeps its colour. */
const LOGOCOL = ['#5B4BE8', '#0E7C86', '#C2410C', '#9333EA', '#0F766E', '#B45309', '#BE185D', '#1D4ED8'];
export function logoColour(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return LOGOCOL[h % LOGOCOL.length]!;
}

export const plural = (n: number, one: string, many = one + 's'): string => `${n} ${n === 1 ? one : many}`;

/** Today's date in Asia/Kolkata as YYYY-MM-DD (the API's business-date zone). */
export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}
